import { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import type { Cycle } from '@/lib/types';

/**
 * Loads cycles for the intake surfaces.
 *
 * The intake response deliberately does not embed its parent cycle, so the
 * name has to be joined client-side. One fetch of the first page serves both
 * jobs at once: the "Cycle" filter on the list, and the picker in the form.
 *
 * `perPage: 100` is the API's maximum. Cycles are business years — a handful of
 * rows, not a directory — so a single page is the right shape here rather than
 * a typeahead. `hasMore` says when that stops being true instead of silently
 * truncating the list.
 *
 * `failed` is what makes the degradation below honest rather than silent. A
 * failed lookup deliberately does not take the intake list down with it — the
 * rows still render with the cycle id standing in for the name — but a caller
 * that cannot tell a failure from an empty master shows a bare id with no
 * explanation, and a picker with nothing in it that reads as "there are no
 * cycles". Both are statements about the data, made on the strength of a
 * request that never came back. Read `failed` and say what happened.
 */
export function useCycleOptions(enabled = true) {
  const [cycles, setCycles] = useState<Cycle[]>([]);
  // Starts true when enabled so the first paint shows a loading picker rather
  // than an empty one, and nothing calls setState synchronously in an effect.
  const [loading, setLoading] = useState(enabled);
  const [failed, setFailed] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    api
      .listCycles({ page: 1, perPage: 100 }, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setCycles(result.data);
        setHasMore(result.meta.totalPages > 1);
        setFailed(false);
      })
      .catch(() => {
        // A failed lookup must not take the intake list down with it: the rows
        // still render, just with the cycle id in place of its name. `failed`
        // is what lets the page say so instead of presenting the id as though
        // that were all the cycle ever had.
        if (controller.signal.aborted) return;
        setCycles([]);
        setFailed(true);
        // Cleared with the rows it described — see `use-user-options.ts`.
        setHasMore(false);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [enabled]);

  /** id → name, for rendering an intake's cycle without a second request. */
  const namesById = useMemo(() => {
    const map = new Map<number, string>();
    for (const cycle of cycles) map.set(cycle.id, cycle.cycleName);
    return map;
  }, [cycles]);

  return { cycles, namesById, loading, failed, hasMore };
}
